'use client'

import { useHeaderTheme } from '@/providers/HeaderTheme'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import React, { useEffect, useState } from 'react'

import type { Branding, Header } from '@/payload-types'

import { Logo } from '@/components/Logo/Logo'
import { HeaderNav } from './Nav'
import { MobileMenu } from './MobileMenu'
import { MenuIcon } from 'lucide-react'
import { cn } from '@/utilities/ui'

interface HeaderClientProps {
  data: Header
  logo?: Branding['headerLogo']
}

export const HeaderClient: React.FC<HeaderClientProps> = ({ data, logo }) => {
  const { headerTheme, setHeaderTheme } = useHeaderTheme()
  const pathname = usePathname()
  const [isScrolled, setIsScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useEffect(() => {
    setHeaderTheme(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileMenuOpen])

  const stickyHeader = data?.stickyHeader !== false
  const announcementBar = data?.announcementBar

  return (
    <header
      className={cn(
        'z-50 w-full bg-background/95 backdrop-blur-sm transition-all duration-300',
        'border-b border-border/40',
        isScrolled && 'shadow-sm',
        stickyHeader ? 'sticky top-0' : 'relative',
      )}
      {...(headerTheme ? { 'data-theme': headerTheme } : {})}
    >
      {announcementBar?.enabled && announcementBar.text && (
        <div className="bg-brand-red text-white">
          <div className="container py-2 text-center text-sm">
            {announcementBar.url ? (
              <Link
                href={announcementBar.url}
                className="inline-flex items-center gap-1 hover:underline"
              >
                {announcementBar.text}
                <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" />
              </Link>
            ) : (
              announcementBar.text
            )}
          </div>
        </div>
      )}

      <div
        className={cn(
          'container flex items-center transition-all duration-300',
          isScrolled ? 'h-16' : 'h-20',
        )}
      >
        <div className="flex items-center justify-between w-full">
          <Link href="/" className="flex-shrink-0">
            <Logo loading="eager" priority="high" resource={logo} />
          </Link>

          <HeaderNav data={data} />

          <div className="flex lg:hidden items-center gap-2">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-md text-foreground/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2"
              aria-label="Open menu"
              aria-expanded={mobileMenuOpen}
            >
              <MenuIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <MobileMenu
        data={data}
        logo={
          logo && typeof logo === 'object' && 'url' in logo
            ? {
                url: logo.url,
                alt: logo.alt,
                width: logo.width,
                height: logo.height,
              }
            : undefined
        }
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />
    </header>
  )
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  )
}
